import { useState, useEffect, useCallback } from "react";
import { useAppDispatch } from "../../redux/hooks";
import { getSubCategoryProducts } from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import toast from "react-hot-toast";

interface SubCategoryProduct {
    productId: string;
    name: string;
    image: string;
}

interface SubCategoryProductsViewProps {
    subCategory: any;
    parentCategoryName?: string;
    onBack: () => void;
}

const SubCategoryProductsView = ({
    subCategory,
    parentCategoryName,
    onBack,
}: SubCategoryProductsViewProps) => {
    const dispatch = useAppDispatch();
    const [products, setProducts] = useState<SubCategoryProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState<any>(null);
    const limit = 20;

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const subId = subCategory._id || subCategory.subCategoryId || "";
            const res: any = await dispatch(
                getSubCategoryProducts({ subCategoryId: subId, page, limit }),
            );
            if (res?.error) {
                toast.error(
                    res?.payload || res?.error?.message || "Failed to load products",
                );
                setProducts([]);
                return;
            }
            const data = res?.payload?.data;
            setProducts(Array.isArray(data?.products) ? data.products : []);
            setPagination(data?.pagination || null);
        } catch {
            toast.error("Failed to load products");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [dispatch, subCategory, page, limit]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    return (
        <div className="col-span-full w-full">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Subcategories
                </button>

                <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-gray-900">
                        {subCategory.name}
                    </h3>
                    {parentCategoryName && (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                            {parentCategoryName}
                        </span>
                    )}
                    {pagination && (
                        <span className="text-xs text-gray-500">
                            {pagination.totalProducts} product{pagination.totalProducts !== 1 ? "s" : ""}
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <p className="text-sm font-medium">No products in this subcategory</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {products.map((product) => (
                            <div
                                key={product.productId}
                                className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="aspect-square bg-gray-50 overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src =
                                                "https://via.placeholder.com/200x200?text=No+Image";
                                        }}
                                    />
                                </div>
                                <div className="px-3 py-2.5">
                                    <p className="text-sm font-medium text-gray-800 truncate" title={product.name}>
                                        {product.name}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-6">
                            <div className="text-sm text-gray-500">
                                Page {pagination.currentPage} of {pagination.totalPages} •{" "}
                                {pagination.totalProducts} total
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={!pagination.hasPreviousPage}
                                    className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                                    disabled={!pagination.hasNextPage}
                                    className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SubCategoryProductsView;
